import {
  Badge,
  Button,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  ArrowRight,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatSessionDate } from '../../research/packages/session-repair/utils/formatRepair';
import { SessionRepairStatusBadge } from '../../research/packages/session-repair';
import {
  compareHrEventCount,
  compareNullableMetrics,
  comparePatientCodes,
  compareRepairStatus,
  compareSessionDates,
  type GroupSessionRow,
} from '../utils/sortGroupSessions';

type SessionGroupSessionsTableProps = {
  rows: GroupSessionRow[];
  getOpenHref: (sessionId: number) => string;
  onOpenClick?: (sessionId: number) => void;
  onRemove?: (sessionId: number) => void;
  removingSessionId?: number | null;
  maxHeight?: number;
  showHrvMetrics?: boolean;
  showDevice?: boolean;
};

const columnHelper = createColumnHelper<GroupSessionRow>();

const NUMERIC_COLUMN_IDS = new Set([
  'hrEventCount',
  'meanRrMs',
  'rmssdMs',
  'lfHfRatio',
]);

const formatHrEventCount = (session: GroupSessionRow) => {
  if (session.status === 'ready' || session.hrEventCount >= 0) {
    if (session.hrEventCount < 0) {
      return '…';
    }

    return session.hrEventCount;
  }

  if (session.status === 'loading') {
    return '…';
  }

  return '—';
};

const formatHrvMetric = (value: number | null | undefined, digits = 1) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(digits);
};

const isLoadingRow = (row: GroupSessionRow) => row.status === 'loading';

const skeletonWidthForColumn = (columnId: string) => {
  switch (columnId) {
    case 'sessionId':
      return 48;
    case 'patientCode':
      return 72;
    case 'deviceName':
      return 96;
    case 'sessionDate':
      return 120;
    case 'hasHrData':
      return 88;
    case 'repairStatus':
      return 104;
    case 'hrEventCount':
    case 'meanRrMs':
    case 'rmssdMs':
    case 'lfHfRatio':
      return 56;
    case 'actions':
      return 72;
    default:
      return 80;
  }
};

const renderLoadingCell = (columnId: string) => (
  <Skeleton
    height={16}
    width={skeletonWidthForColumn(columnId)}
    radius='sm'
    ml={NUMERIC_COLUMN_IDS.has(columnId) ? 'auto' : undefined}
  />
);

const buildHrvMetricColumn = (
  id: 'meanRrMs' | 'rmssdMs' | 'lfHfRatio',
  header: string
): ColumnDef<GroupSessionRow, any> =>
  columnHelper.accessor(id, {
    header,
    cell: ({ getValue }) => (
      <Text size='sm' ta='right'>
        {formatHrvMetric(getValue())}
      </Text>
    ),
    sortingFn: (left, right) =>
      compareNullableMetrics(left.original[id], right.original[id]),
  });

export function SessionGroupSessionsTable({
  rows,
  getOpenHref,
  onOpenClick,
  onRemove,
  removingSessionId,
  maxHeight,
  showHrvMetrics = false,
  showDevice = false,
}: SessionGroupSessionsTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'sessionId', desc: false },
  ]);

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<GroupSessionRow, any>[] = [
      columnHelper.accessor('sessionId', {
        header: 'ID',
      }),
      columnHelper.accessor('patientCode', {
        header: 'Utente',
        cell: ({ getValue }) => getValue() ?? 'n/a',
        sortingFn: (left, right) =>
          comparePatientCodes(
            left.original.patientCode,
            right.original.patientCode
          ),
      }),
    ];

    if (showDevice) {
      baseColumns.push(
        columnHelper.accessor('deviceName', {
          header: 'Sensore',
          cell: ({ row }) => {
            const model = row.original.deviceName?.trim();
            const name = row.original.deviceSensorName?.trim();

            if (!model && !name) {
              return '—';
            }

            return (
              <Stack gap={0}>
                <Text size='sm'>{model || name}</Text>
                {model && name ? (
                  <Text size='xs' c='dimmed'>
                    {name}
                  </Text>
                ) : null}
              </Stack>
            );
          },
          sortingFn: (left, right) =>
            comparePatientCodes(
              left.original.deviceName,
              right.original.deviceName
            ),
        })
      );
    }

    baseColumns.push(
      columnHelper.accessor('sessionDate', {
        header: 'Data',
        cell: ({ getValue }) => formatSessionDate(getValue()),
        sortingFn: (left, right) =>
          compareSessionDates(
            left.original.sessionDate,
            right.original.sessionDate
          ),
      }),
      columnHelper.accessor('hasHrData', {
        header: 'Record biometrici',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge
            color={row.original.hasHrData ? 'teal' : 'red'}
            variant='light'
          >
            {row.original.hasHrData ? 'Presenti' : 'Assenti'}
          </Badge>
        ),
      }),
      columnHelper.accessor('repairStatus', {
        header: 'Stato',
        cell: ({ row }) => (
          <Stack gap={4}>
            {row.original.repairStatus ? (
              <SessionRepairStatusBadge
                status={row.original.repairStatus}
                duplicateCount={row.original.duplicateCount}
                gapCount={row.original.gapCount}
              />
            ) : (
              <Badge color='gray' variant='light'>
                Da analizzare
              </Badge>
            )}
            {row.original.error && (
              <Text size='xs' c='red' lineClamp={2}>
                {row.original.error}
              </Text>
            )}
          </Stack>
        ),
        sortingFn: (left, right) =>
          compareRepairStatus(
            left.original.repairStatus,
            right.original.repairStatus
          ),
      }),
      columnHelper.accessor('hrEventCount', {
        header: 'Eventi HR',
        cell: ({ row }) => formatHrEventCount(row.original),
        sortingFn: (left, right) =>
          compareHrEventCount(
            left.original.hrEventCount,
            right.original.hrEventCount
          ),
      })
    );

    if (showHrvMetrics) {
      baseColumns.push(
        buildHrvMetricColumn('meanRrMs', 'RR medio (ms)'),
        buildHrvMetricColumn('rmssdMs', 'RMSSD (ms)'),
        buildHrvMetricColumn('lfHfRatio', 'LF/HF')
      );
    }

    baseColumns.push(
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Group justify='flex-end' gap='xs' wrap='nowrap'>
            <Button
              component={Link}
              to={getOpenHref(row.original.sessionId)}
              variant='light'
              size='compact-sm'
              rightSection={<ArrowRight size={14} />}
              onClick={event => {
                event.stopPropagation();
                onOpenClick?.(row.original.sessionId);
              }}
            >
              Apri
            </Button>
            {onRemove && (
              <Button
                variant='light'
                color='red'
                size='compact-sm'
                leftSection={<Trash2 size={14} />}
                loading={removingSessionId === row.original.sessionId}
                onClick={event => {
                  event.stopPropagation();
                  onRemove(row.original.sessionId);
                }}
              >
                Rimuovi
              </Button>
            )}
          </Group>
        ),
      })
    );

    return baseColumns;
  }, [
    getOpenHref,
    onOpenClick,
    onRemove,
    removingSessionId,
    showDevice,
    showHrvMetrics,
  ]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (rows.length === 0) {
    return (
      <Text c='dimmed' size='sm' p='md' ta='center'>
        Nessuna sessione nel gruppo.
      </Text>
    );
  }

  const tableContent = (
    <Table striped highlightOnHover stickyHeader>
      <Table.Thead>
        {table.getHeaderGroups().map(headerGroup => (
          <Table.Tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Table.Th
                key={header.id}
                style={{
                  cursor: header.column.getCanSort() ? 'pointer' : 'default',
                  userSelect: 'none',
                  whiteSpace: showHrvMetrics ? 'nowrap' : undefined,
                }}
                ta={NUMERIC_COLUMN_IDS.has(header.id) ? 'right' : 'left'}
                onClick={header.column.getToggleSortingHandler()}
              >
                <Group
                  gap={4}
                  justify={
                    NUMERIC_COLUMN_IDS.has(header.id)
                      ? 'flex-end'
                      : 'flex-start'
                  }
                  wrap='nowrap'
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {header.column.getCanSort() && (
                    <>
                      {header.column.getIsSorted() === 'asc' && (
                        <ChevronUp size={14} />
                      )}
                      {header.column.getIsSorted() === 'desc' && (
                        <ChevronDown size={14} />
                      )}
                      {!header.column.getIsSorted() && (
                        <ArrowUpDown
                          size={14}
                          style={{ opacity: 0.4, flexShrink: 0 }}
                        />
                      )}
                    </>
                  )}
                </Group>
              </Table.Th>
            ))}
          </Table.Tr>
        ))}
      </Table.Thead>
      <Table.Tbody>
        {table.getRowModel().rows.map(row => {
          const loading = isLoadingRow(row.original);

          return (
            <Table.Tr
              key={row.id}
              style={{ cursor: loading ? 'default' : 'pointer' }}
              onClick={() => {
                if (loading) return;

                onOpenClick?.(row.original.sessionId);
                navigate(getOpenHref(row.original.sessionId));
              }}
            >
              {row.getVisibleCells().map(cell => (
                <Table.Td
                  key={cell.id}
                  ta={NUMERIC_COLUMN_IDS.has(cell.column.id) ? 'right' : 'left'}
                >
                  {loading
                    ? renderLoadingCell(cell.column.id)
                    : flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );

  if (maxHeight == null) {
    return (
      <ScrollArea type='auto' offsetScrollbars>
        {tableContent}
      </ScrollArea>
    );
  }

  return (
    <ScrollArea.Autosize mah={maxHeight} type='auto' offsetScrollbars>
      {tableContent}
    </ScrollArea.Autosize>
  );
}
